// Waiting, done well (design round 5). The database rests when nobody uses it and the first request
// after a quiet spell can take up to 30 seconds (ADR-010): say what is happening, show progress, and
// never leave an empty page.
import { useEffect, useState } from 'react';
import { onSlowRequests } from './api.js';
import { LogoMark } from './Logo.js';

const STEPS = [
  { after: 0, label: 'Starting your database in Sydney' },
  { after: 8, label: 'Checking everything adds up' },
  { after: 16, label: 'Loading your numbers' },
];

function useSlow(): boolean {
  const [slow, setSlow] = useState(false);
  useEffect(() => onSlowRequests(setSlow), []);
  return slow;
}

function useSeconds(running: boolean): number {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!running) return;
    setSeconds(0);
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [running]);
  return seconds;
}

/** The first screen after sign-in, until the books are loaded. */
export function BootScreen({ error, onRetry }: { error?: string; onRetry?: () => void }) {
  const slow = useSlow();
  const seconds = useSeconds(slow);
  return (
    <main className="boot" aria-busy={!error}>
      <div className="boot-card">
        <span className={error ? 'boot-mark' : 'boot-mark pulse'}>
          <LogoMark size={56} />
        </span>
        <h1>
          {error ? 'Something went wrong' : slow ? 'Waking up your books' : 'Opening your books'}
        </h1>
        {error ? (
          <>
            <p className="muted">{error}</p>
            {onRetry && (
              <button type="button" onClick={onRetry}>
                Try again
              </button>
            )}
          </>
        ) : (
          <>
            <div
              className={slow ? 'boot-bar filling' : 'boot-bar'}
              role="progressbar"
              aria-label="Opening your books"
            >
              <span />
            </div>
            {slow ? (
              <>
                <ol className="boot-steps" aria-live="polite">
                  {STEPS.map((step, i) => {
                    const next = STEPS[i + 1];
                    const state =
                      seconds < step.after
                        ? 'step-wait'
                        : next && seconds >= next.after
                          ? 'step-done'
                          : 'step-now';
                    return (
                      <li key={step.label} className={state}>
                        <span className="tick" aria-hidden="true" />
                        {step.label}
                        {state === 'step-done' && <span className="sr-only"> (done)</span>}
                      </li>
                    );
                  })}
                </ol>
                <p className="boot-note">
                  Your database rests when nobody is using it, so the first visit after a quiet
                  spell takes up to 30 seconds. After that, everything is instant.
                </p>
              </>
            ) : (
              <p className="muted">One moment.</p>
            )}
          </>
        )}
      </div>
    </main>
  );
}

/** Later in a visit: a slim bar at the top and a small note, never a blocking banner. */
export function SlowBar() {
  const slow = useSlow();
  if (!slow) return null;
  return (
    <div className="slowbar" role="status">
      <span className="slowbar-line" aria-hidden="true" />
      <span className="slowbar-note">
        <span className="spinner" aria-hidden="true" />
        Waking up your books, this can take up to 30 seconds
      </span>
    </div>
  );
}
